import React, { useEffect, useState, useCallback, memo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchDesignReadyCases } from "../../store/common/casesSlice";
import {
  sendDesignReadyEmails,
  resetStatus,
} from "../../store/admin/emailSlice";
import { useToast } from "../../components/utilities/Toast";
import { CaseType } from "../../types/Case";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminMetaRow,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
  AdminSoftInput,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

interface DisplayCase extends CaseType {
  selected?: boolean;
  note?: string;
}

const PAGE_SIZE = 50;

const statusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "files pending") return "bg-red-50 text-red-700 ring-red-200";
  if (s === "submitted") return "bg-orange-50 text-orange-700 ring-orange-200";
  if (s === "query pending")
    return "bg-amber-50 text-amber-800 ring-amber-200";
  if (s === "under process")
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (s === "completed") return "bg-sky-50 text-sky-700 ring-sky-200";
  if (s.includes("milling"))
    return "bg-violet-50 text-violet-700 ring-violet-200";
  return "bg-gray-50 text-gray-600 ring-gray-200";
};

const CaseRow = memo(
  ({
    record,
    index,
    toggleSelect,
    updateNote,
  }: {
    record: DisplayCase;
    index: number;
    toggleSelect: (index: number) => void;
    updateNote: (index: number, note: string) => void;
  }) => (
    <tr className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors">
      <td className={`${adminTableCell} text-center`}>
        <input
          type="checkbox"
          checked={record.selected || false}
          onChange={() => toggleSelect(index)}
          className="h-4 w-4 accent-[#2fa9bf] cursor-pointer"
        />
      </td>
      <td className={adminTableCell}>{record.case_id}</td>
      <td className={adminTableCell}>{record.case_owner || "N/A"}</td>
      <td className={adminTableCell}>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset capitalize ${statusStyles(
            record.status || ""
          )}`}
        >
          {record.status || "unknown"}
        </span>
      </td>
      <td className={adminTableCell}>{record.service_type || "N/A"}</td>
      <td className={adminTableCell}>
        <AdminSoftInput
          type="text"
          value={record.note || ""}
          onChange={(e) => updateNote(index, e.target.value)}
          className="w-full !py-1.5"
          placeholder="Add note"
        />
      </td>
    </tr>
  )
);

const DesignReady: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const { designReadyCases, designReadyTotalPages, isLoading, error } =
    useAppSelector((state) => state.adminCases);
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);

  const emailState = useAppSelector((state) => state.emailSender);
  const [localCases, setLocalCases] = useState<DisplayCase[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    dispatch(fetchDesignReadyCases({ page: currentPage, limit: PAGE_SIZE }));
  }, [dispatch, currentPage]);

  useEffect(() => {
    setLocalCases(
      designReadyCases.map((c) => ({ ...c, selected: false, note: "" }))
    );
    setIsAllSelected(false);
  }, [designReadyCases]);

  useEffect(() => {
    if (emailState.success) {
      showToast(
        `Emails sent to: ${emailState.finalEmails?.join(", ") || "N/A"}`,
        "success"
      );
      dispatch(fetchDesignReadyCases({ page: currentPage, limit: PAGE_SIZE }));
      dispatch(resetStatus());
    } else if (emailState.error) {
      const msg =
        emailState.missingCaseIds?.length > 0
          ? `Missing emails for: ${emailState.missingCaseIds.join(", ")}`
          : emailState.error;
      showToast(msg, "error");
      dispatch(resetStatus());
    }
  }, [emailState, dispatch, currentPage]);

  const toggleSelect = useCallback((index: number) => {
    setLocalCases((prev) => {
      const updated = prev.map((c, i) =>
        i === index ? { ...c, selected: !c.selected } : c
      );
      setIsAllSelected(updated.every((c) => c.selected));
      return updated;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setLocalCases((prev) => {
      const newValue = !isAllSelected;
      const updated = prev.map((c) => ({ ...c, selected: newValue }));
      setIsAllSelected(newValue);
      return updated;
    });
  }, [isAllSelected]);

  const updateNote = useCallback((index: number, note: string) => {
    setLocalCases((prev) =>
      prev.map((c, i) => (i === index ? { ...c, note } : c))
    );
  }, []);

  const handleSendEmails = useCallback(() => {
    if (!localCases.some((c) => c.selected)) {
      showToast("No cases selected", "error");
      return;
    }
    setShowConfirmModal(true);
  }, [localCases, showToast]);

  const confirmSend = useCallback(() => {
    const payload = localCases
      .filter((c) => c.selected)
      .map((c) => ({
        case_id: c.case_id,
        note: c.note || "",
      }));

    const finalSendBy =
      user?.role === "Super Admin" ? user?.user_name : employee?.emp_name;

    dispatch(
      sendDesignReadyEmails({
        cases: payload,
        sendBy: finalSendBy || "",
      })
    );

    setShowConfirmModal(false);
  }, [localCases, dispatch]);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= designReadyTotalPages) setCurrentPage(page);
    },
    [designReadyTotalPages]
  );

  const selectedCount = localCases.filter((c) => c.selected).length;
  const colCount = 6;

  return (
    <>
      <Helmet>
        <title>Design Ready</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Design Ready"
          subtitle="Send design-ready notifications for selected cases"
        />

        <AdminMetaRow
          left={
            <span>
              {designReadyCases.length} case
              {designReadyCases.length === 1 ? "" : "s"} on this page
            </span>
          }
          right={
            <>
              <AdminPrimaryButton
                onClick={handleSendEmails}
                disabled={emailState.loading}
              >
                {emailState.loading
                  ? "Sending…"
                  : `Send Email to Selected${
                      selectedCount ? ` (${selectedCount})` : ""
                    }`}
              </AdminPrimaryButton>
              <AdminPagination
                page={currentPage}
                totalPages={designReadyTotalPages}
                onPrev={() => goToPage(currentPage - 1)}
                onNext={() => goToPage(currentPage + 1)}
                disabled={isLoading}
              />
            </>
          }
        />

        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={`${adminTableHead} text-center`}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-[#2fa9bf] cursor-pointer"
                  />
                </th>
                <th className={adminTableHead}>Case ID</th>
                <th className={adminTableHead}>Patient/Job</th>
                <th className={adminTableHead}>Status</th>
                <th className={adminTableHead}>Service Type</th>
                <th className={adminTableHead}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading cases…"
                />
              ) : error ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="error"
                  message="Error fetching design ready cases"
                />
              ) : localCases.length === 0 ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message="No Design Ready cases found."
                />
              ) : (
                localCases.map((record, index) => (
                  <CaseRow
                    key={record.case_id}
                    record={record}
                    index={index}
                    toggleSelect={toggleSelect}
                    updateNote={updateNote}
                  />
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>

        {/* Confirm Modal */}
        {showConfirmModal ? (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border border-[#E8E8E4]">
              <div>
                <h3 className="text-base font-semibold text-[#4A4A48]">
                  Confirm email send
                </h3>
                <p className="text-sm text-[#8A8A85] mt-1.5 leading-relaxed">
                  Send design-ready emails for{" "}
                  <span className="font-medium text-[#2fa9bf]">
                    {selectedCount}
                  </span>{" "}
                  selected case{selectedCount === 1 ? "" : "s"}?
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
                >
                  Cancel
                </button>
                <AdminPrimaryButton onClick={confirmSend}>
                  Yes, Send
                </AdminPrimaryButton>
              </div>
            </div>
          </div>
        ) : null}
      </AdminPageShell>
    </>
  );
};

export default DesignReady;
